import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";

// Contract source: apps/api/src/routes/ads.ts. Numeric rollups arrive as
// strings (BigInt/Decimal serialization) — format client-side with Number().

export type AdBoostStatus =
  | "SUGGESTED"
  | "CREATING"
  | "PENDING"
  | "ACTIVE"
  | "PAUSED"
  | "COMPLETED"
  | "REJECTED"
  | "FAILED"
  | "CANCELLED";

export type AdBoostGoal = "engagement" | "traffic" | "awareness" | "video_views";

export interface AdBoostPiece {
  id: string;
  shortCode: string;
  caption: string;
  thumbnailUrl: string | null;
  platform: string;
}

export interface AdBoost {
  id: string;
  pieceId: string;
  scheduledPostId: string;
  status: AdBoostStatus;
  mode: "suggest" | "auto";
  goal: AdBoostGoal;
  budgetUsd: string | null;
  currency: string;
  startDate: string | null;
  endDate: string | null;
  zernioAdId: string | null;
  error: string | null;
  createdAt: string;
  piece: AdBoostPiece | null;
  spendToDateUsd: string;
  impressions: string;
  clicks: string;
  cpm: number | null;
  cpc: number | null;
}

export interface AdsSummary {
  committedThisMonthUsd: string;
  capUsd: string | null;
  activeCount: number;
}

export interface AdAccountInfo {
  id: string;
  socialAccountId: string;
  provider: string;
  platformAdAccountId: string;
  name: string | null;
  currency: string;
  status: string;
}

export interface AdsOverview {
  account: AdAccountInfo | null;
  boosts: AdBoost[];
  summary: AdsSummary;
}

export interface BoostDaily {
  date: string;
  spendUsd: string | null;
  impressions: string;
  reach: string;
  clicks: string;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  engagement: string;
}

export type BoostDetail = AdBoost & { daily: BoostDaily[] };

export interface AvailableAdAccountGroup {
  socialAccountId: string;
  platform: string;
  adAccounts: Array<{ id: string; name: string | null; currency: string; status: string }>;
}

export function useAds(brandId: string) {
  return useQuery({
    queryKey: ["ads", brandId],
    queryFn: () => apiFetch<AdsOverview>(`/brands/${brandId}/ads`),
    enabled: !!brandId,
  });
}

export function useBoost(brandId: string, boostId: string) {
  return useQuery({
    queryKey: ["ads-boost", brandId, boostId],
    queryFn: () => apiFetch<BoostDetail>(`/brands/${brandId}/ads/boosts/${boostId}`),
    enabled: !!brandId && !!boostId,
  });
}

/**
 * Lazy by design: the endpoint round-trips the ads provider serially per
 * connected account. Only enable once the picker UI is actually opened.
 */
export function useAvailableAdAccounts(brandId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["ads-available", brandId],
    queryFn: () =>
      apiFetch<AvailableAdAccountGroup[]>(`/brands/${brandId}/ads/accounts/available`),
    enabled: !!brandId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSelectAdAccount(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { socialAccountId: string; platformAdAccountId: string }) =>
      apiFetch<AdAccountInfo>(`/brands/${brandId}/ads/account`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ads", brandId] }),
  });
}

export type BoostActionName = "activate" | "pause" | "resume" | "cancel";

const ACTION_SUCCESS: Record<BoostActionName, string> = {
  activate: "Boost activated",
  pause: "Boost paused",
  resume: "Boost resumed",
  cancel: "Boost cancelled",
};

function boostActionErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === "BUDGET_EXCEEDED") {
      return "Monthly ads budget exceeded — raise the cap in Settings → Automation or wait for next month";
    }
    if (err.code === "ADS_DISABLED") {
      return "Ads are globally disabled by the operator";
    }
    // 409 family (ADS_NOT_ENABLED / ADS_PAUSED / WRONG_STATUS / NOT_BOOSTABLE)
    // and everything else: the server message is already user-readable.
    return err.message;
  }
  return err instanceof Error ? err.message : "Request failed";
}

export function useBoostAction(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ boostId, action }: { boostId: string; action: BoostActionName }) =>
      apiFetch<AdBoost>(`/brands/${brandId}/ads/boosts/${boostId}/${action}`, {
        method: "POST",
      }),
    onSuccess: (_data, { action }) => {
      toast.success(ACTION_SUCCESS[action]);
    },
    onSettled: (_data, _err, { boostId }) => {
      qc.invalidateQueries({ queryKey: ["ads", brandId] });
      qc.invalidateQueries({ queryKey: ["ads-boost", brandId, boostId] });
    },
    onError: (err) => {
      toast.error(boostActionErrorMessage(err));
    },
  });
}
